import type { Locale } from '../../lib/i18n';
import { es } from './es';
import { pl } from './pl';
import { ro } from './ro';
import { LEGAL, type LegalDoc, type LegalKind } from './meta';

export { LEGAL, type LegalDoc, type LegalKind, type LegalSet } from './meta';

const { company: C, email: E } = LEGAL;

const BASE: Record<LegalKind, Record<'en' | 'uk' | 'ru', LegalDoc>> = {
  privacy: {
    en: {
      title: 'Privacy Policy',
      intro: `This policy explains what personal data ${C} ("we") collects when you use the website, the editor and the API (the "Service"), why we collect it and what rights you have.`,
      sections: [
        {
          h: '1. Data we collect',
          p: [
            'Account data: name, email address, password hash, interface language, and — if you sign in with Google — your Google account ID and verified email.',
            'Content: templates, images, fonts, tables and other files you upload, data you send to the API to render images, and the generated images.',
            'Usage data: renders, automation runs, AI requests, API keys (stored as hashes), sign-in sessions, IP address and browser user agent, error logs.',
            'Billing data: plan, subscription status and payment identifiers. Card details are handled by the payment provider (Lemon Squeezy or Stripe) and never reach our servers.',
          ],
        },
        {
          h: '2. How we use data',
          p: [
            'To provide the Service: store your templates, render images, run automations and deliver results to the integrations you connect.',
            'To keep the Service secure and reliable: authentication, abuse prevention, rate limits, debugging and backups.',
            'To bill you and enforce plan limits, and to send service emails (password reset, invitations, important changes). We do not send marketing emails without your consent.',
          ],
        },
        {
          h: '3. Legal bases',
          p: [
            'We process data to perform our contract with you, to comply with legal obligations, and on the basis of our legitimate interest in running a secure service. Where required, we ask for your consent.',
          ],
        },
        {
          h: '4. AI features',
          p: [
            'When you use AI generation, the brief or text you enter is sent to our AI provider (Google Gemini API) to produce the result. We do not send your other templates or files. The provider processes this data under its own terms and does not use paid API traffic to train its models.',
          ],
        },
        {
          h: '5. Sharing',
          p: [
            'We do not sell personal data. We share it only with processors that help us run the Service — hosting and storage, payments (Lemon Squeezy, Stripe), email delivery, AI generation (Google) — and with integrations you connect yourself (Google Drive, Sheets, S3, webhooks).',
            'We may disclose data when required by law or to protect our users and the Service.',
            'Some processors (for example Google) may process data outside the EU/EEA. Such transfers rely on the EU–US Data Privacy Framework or the European Commission’s Standard Contractual Clauses.',
          ],
        },
        {
          h: '6. Retention',
          p: [
            'We keep account data and content while your account is active. Generated images and logs are kept according to your plan and settings. After you delete a workspace or account, the data is deleted within 30 days, except where we must keep it longer by law (for example, invoices).',
          ],
        },
        {
          h: '7. Cookies',
          p: [
            'Essential cookies (no consent needed): the session cookie that keeps you signed in and a short-lived cookie for Google sign-in. We store your language, selected workspace and cookie choice in the browser.',
            'Analytics cookies — only with your consent: Google Analytics 4 (Google Ireland Ltd.) measures visits with pseudonymous identifiers in the _ga cookies (up to 2 years); Google keeps the data for 14 months, Google signals and advertising features are off. Analytics is not loaded until you accept it in the cookie banner, and you can withdraw consent at any time via "Cookie settings" in the footer or your profile. We do not use advertising cookies.',
          ],
        },
        {
          h: '8. Security',
          p: [
            'Passwords and API keys are stored as hashes, integration credentials are encrypted, and traffic is protected with HTTPS. No system is perfectly secure, but we take reasonable measures to protect your data.',
          ],
        },
        {
          h: '9. Your rights',
          p: [
            `You have the rights of access, rectification, erasure, restriction, data portability and objection (GDPR Art. 15–21). In Settings → Profile you can download a copy of your data and delete your account yourself. You may also object to processing, withdraw consent and lodge a complaint with your data protection authority. To exercise your rights, write to ${E}.`,
          ],
        },
        {
          h: '10. Children',
          p: [
            'The Service is not intended for people under 16, and we do not knowingly collect their data.',
          ],
        },
        {
          h: '11. Changes and contact',
          p: [
            `We will publish changes on this page and notify you by email about significant ones. Questions about privacy: ${E}.`,
          ],
        },
      ],
    },
    uk: {
      title: 'Політика конфіденційності',
      intro: `Ця політика пояснює, які персональні дані ${C} («ми») збирає, коли ви користуєтеся сайтом, редактором і API («Сервіс»), навіщо ми це робимо і які у вас є права.`,
      sections: [
        {
          h: '1. Які дані ми збираємо',
          p: [
            'Дані акаунта: імʼя, email, хеш пароля, мова інтерфейсу, а якщо ви входите через Google — ідентифікатор Google-акаунта та підтверджений email.',
            'Контент: шаблони, зображення, шрифти, таблиці та інші файли, які ви завантажуєте, дані, які ви надсилаєте в API для генерації, і згенеровані зображення.',
            'Дані використання: генерації, запуски автоматизацій, AI-запити, API-ключі (зберігаються як хеші), сесії входу, IP-адреса та дані браузера, журнали помилок.',
            'Платіжні дані: тариф, статус підписки та платіжні ідентифікатори. Дані картки обробляє платіжний провайдер (Lemon Squeezy або Stripe), вони не потрапляють на наші сервери.',
          ],
        },
        {
          h: '2. Як ми використовуємо дані',
          p: [
            'Щоб надавати Сервіс: зберігати шаблони, генерувати зображення, запускати автоматизації та передавати результати в підключені вами інтеграції.',
            'Щоб Сервіс був безпечним і стабільним: автентифікація, захист від зловживань, ліміти запитів, діагностика й резервні копії.',
            'Щоб виставляти рахунки й застосовувати ліміти тарифу, а також надсилати службові листи (скидання пароля, запрошення, важливі зміни). Маркетингові листи без вашої згоди ми не надсилаємо.',
          ],
        },
        {
          h: '3. Правові підстави',
          p: [
            'Ми обробляємо дані для виконання договору з вами, виконання вимог закону та на підставі законного інтересу в безпечній роботі Сервісу. Там, де це потрібно, ми просимо вашої згоди.',
          ],
        },
        {
          h: '4. AI-функції',
          p: [
            'Коли ви користуєтеся AI-генерацією, введений бриф або текст надсилається нашому AI-провайдеру (Google Gemini API) для отримання результату. Інші ваші шаблони й файли не передаються. Провайдер обробляє ці дані за власними умовами й не використовує платний API-трафік для навчання моделей.',
          ],
        },
        {
          h: '5. Передача третім особам',
          p: [
            'Ми не продаємо персональні дані. Передаємо їх лише обробникам, які допомагають працювати Сервісу, — хостинг і сховище, платежі (Lemon Squeezy, Stripe), доставка листів, AI-генерація (Google), — а також інтеграціям, які ви підключаєте самі (Google Drive, Sheets, S3, вебхуки).',
            'Ми можемо розкрити дані, якщо цього вимагає закон або це потрібно для захисту користувачів і Сервісу.',
            'Деякі обробники (наприклад, Google) можуть обробляти дані за межами ЄС/ЄЕЗ. Така передача ґрунтується на EU–US Data Privacy Framework або Стандартних договірних положеннях Європейської комісії.',
          ],
        },
        {
          h: '6. Зберігання',
          p: [
            'Дані акаунта й контент зберігаються, поки акаунт активний. Згенеровані зображення та журнали зберігаються відповідно до тарифу й налаштувань. Після видалення простору або акаунта дані видаляються протягом 30 днів, крім випадків, коли закон вимагає зберігати їх довше (наприклад, рахунки).',
          ],
        },
        {
          h: '7. Cookies',
          p: [
            'Необхідні cookies (згода не потрібна): cookie сесії, щоб ви залишалися в акаунті, і короткочасний cookie для входу через Google. Мову, вибраний простір і ваш вибір щодо cookies зберігаємо в браузері.',
            'Аналітичні cookies — лише з вашої згоди: Google Analytics 4 (Google Ireland Ltd.) рахує відвідування за псевдонімними ідентифікаторами в cookies _ga (до 2 років); Google зберігає дані 14 місяців, Google Signals і рекламні функції вимкнено. Аналітика не завантажується, доки ви не погодитеся в банері cookies, а відкликати згоду можна будь-коли через «Налаштування cookies» внизу сайту або в профілі. Рекламних cookies немає.',
          ],
        },
        {
          h: '8. Безпека',
          p: [
            'Паролі й API-ключі зберігаються як хеші, облікові дані інтеграцій шифруються, зʼєднання захищене HTTPS. Абсолютно захищених систем не буває, але ми вживаємо розумних заходів для захисту ваших даних.',
          ],
        },
        {
          h: '9. Ваші права',
          p: [
            `Ви маєте право на доступ, виправлення, видалення, обмеження обробки, перенесення даних і заперечення (ст. 15–21 GDPR). У «Налаштування → Профіль» можна завантажити копію своїх даних і самостійно видалити акаунт. Також ви можете заперечити проти обробки, відкликати згоду й подати скаргу до органу із захисту даних. Щоб скористатися правами, напишіть на ${E}.`,
          ],
        },
        {
          h: '10. Діти',
          p: [
            'Сервіс не призначений для осіб, молодших за 16 років, і ми свідомо не збираємо їхніх даних.',
          ],
        },
        {
          h: '11. Зміни та контакти',
          p: [
            `Зміни ми публікуємо на цій сторінці, про суттєві — повідомляємо email. Питання щодо конфіденційності: ${E}.`,
          ],
        },
      ],
    },
    ru: {
      title: 'Политика конфиденциальности',
      intro: `Эта политика объясняет, какие персональные данные ${C} («мы») собирает, когда вы пользуетесь сайтом, редактором и API («Сервис»), зачем мы это делаем и какие у вас есть права.`,
      sections: [
        {
          h: '1. Какие данные мы собираем',
          p: [
            'Данные аккаунта: имя, email, хеш пароля, язык интерфейса, а при входе через Google — идентификатор Google-аккаунта и подтверждённый email.',
            'Контент: шаблоны, изображения, шрифты, таблицы и другие загружаемые файлы, данные, которые вы отправляете в API для генерации, и сгенерированные изображения.',
            'Данные использования: генерации, запуски автоматизаций, AI-запросы, API-ключи (хранятся как хеши), сессии входа, IP-адрес и данные браузера, журналы ошибок.',
            'Платёжные данные: тариф, статус подписки и платёжные идентификаторы. Данные карты обрабатывает платёжный провайдер (Lemon Squeezy или Stripe), на наши серверы они не попадают.',
          ],
        },
        {
          h: '2. Как мы используем данные',
          p: [
            'Чтобы предоставлять Сервис: хранить шаблоны, генерировать изображения, запускать автоматизации и передавать результаты в подключённые вами интеграции.',
            'Чтобы Сервис был безопасным и стабильным: аутентификация, защита от злоупотреблений, лимиты запросов, диагностика и резервные копии.',
            'Чтобы выставлять счета и применять лимиты тарифа, а также отправлять служебные письма (сброс пароля, приглашения, важные изменения). Маркетинговые письма без вашего согласия мы не отправляем.',
          ],
        },
        {
          h: '3. Правовые основания',
          p: [
            'Мы обрабатываем данные для исполнения договора с вами, выполнения требований закона и на основании законного интереса в безопасной работе Сервиса. Там, где это требуется, мы запрашиваем ваше согласие.',
          ],
        },
        {
          h: '4. AI-функции',
          p: [
            'Когда вы пользуетесь AI-генерацией, введённый бриф или текст отправляется нашему AI-провайдеру (Google Gemini API) для получения результата. Другие ваши шаблоны и файлы не передаются. Провайдер обрабатывает эти данные по своим условиям и не использует платный API-трафик для обучения моделей.',
          ],
        },
        {
          h: '5. Передача третьим лицам',
          p: [
            'Мы не продаём персональные данные. Передаём их только обработчикам, которые помогают работать Сервису, — хостинг и хранилище, платежи (Lemon Squeezy, Stripe), доставка писем, AI-генерация (Google), — а также интеграциям, которые вы подключаете сами (Google Drive, Sheets, S3, вебхуки).',
            'Мы можем раскрыть данные, если этого требует закон или это нужно для защиты пользователей и Сервиса.',
            'Некоторые обработчики (например, Google) могут обрабатывать данные за пределами ЕС/ЕЭЗ. Такая передача основана на EU–US Data Privacy Framework или Стандартных договорных условиях Европейской комиссии.',
          ],
        },
        {
          h: '6. Хранение',
          p: [
            'Данные аккаунта и контент хранятся, пока аккаунт активен. Сгенерированные изображения и журналы хранятся в соответствии с тарифом и настройками. После удаления пространства или аккаунта данные удаляются в течение 30 дней, кроме случаев, когда закон требует хранить их дольше (например, счета).',
          ],
        },
        {
          h: '7. Cookies',
          p: [
            'Необходимые cookies (согласие не требуется): cookie сессии, чтобы вы оставались в аккаунте, и кратковременный cookie для входа через Google. Язык, выбранное пространство и ваш выбор по cookies хранятся в браузере.',
            'Аналитические cookies — только с вашего согласия: Google Analytics 4 (Google Ireland Ltd.) считает посещения по псевдонимным идентификаторам в cookies _ga (до 2 лет); Google хранит данные 14 месяцев, Google Signals и рекламные функции отключены. Аналитика не загружается, пока вы не согласитесь в баннере cookies, а отозвать согласие можно в любой момент через «Настройки cookies» внизу сайта или в профиле. Рекламных cookies нет.',
          ],
        },
        {
          h: '8. Безопасность',
          p: [
            'Пароли и API-ключи хранятся как хеши, учётные данные интеграций шифруются, соединение защищено HTTPS. Абсолютно защищённых систем не бывает, но мы принимаем разумные меры для защиты ваших данных.',
          ],
        },
        {
          h: '9. Ваши права',
          p: [
            `Вы имеете право на доступ, исправление, удаление, ограничение обработки, переносимость данных и возражение (ст. 15–21 GDPR). В «Настройки → Профиль» можно скачать копию своих данных и самостоятельно удалить аккаунт. Также вы можете возразить против обработки, отозвать согласие и подать жалобу в орган по защите данных. Чтобы воспользоваться правами, напишите на ${E}.`,
          ],
        },
        {
          h: '10. Дети',
          p: [
            'Сервис не предназначен для лиц младше 16 лет, и мы сознательно не собираем их данные.',
          ],
        },
        {
          h: '11. Изменения и контакты',
          p: [
            `Изменения мы публикуем на этой странице, о существенных — сообщаем по email. Вопросы о конфиденциальности: ${E}.`,
          ],
        },
      ],
    },
  },
  terms: {
    en: {
      title: 'Terms of Service',
      intro: `These terms govern your use of ${C} (the "Service"). By creating an account or using the Service, you agree to them. If you use the Service on behalf of a company, you accept them on its behalf.`,
      sections: [
        {
          h: '1. The Service',
          p: [
            'The Service lets you design image templates, generate images through the editor, the API and automations, and use AI features. We may change and improve features over time.',
          ],
        },
        {
          h: '2. Account',
          p: [
            'You must provide accurate information and keep your password and API keys confidential. You are responsible for everything done through your account and workspaces. Tell us right away if you suspect unauthorised access.',
          ],
        },
        {
          h: '3. Plans, payment and cancellation',
          p: [
            'Paid plans are billed in advance, monthly or yearly, and renew automatically. Payments are processed by our reseller Lemon Squeezy (the merchant of record, which also handles sales taxes) or by Stripe. We email a reminder a few days before each renewal and a receipt after it. Usage limits for each plan are listed on the pricing page; when a limit is reached, the related actions are paused until the next period or an upgrade.',
            'If a renewal payment fails, the plan keeps working for several days while you update the payment method; if the payment still doesn’t go through, the workspace moves to the Free plan. Your templates and files are kept.',
            'You can cancel at any time: the plan stays active until the end of the paid period and is not renewed. Payments are non-refundable except where required by law. We may change prices with at least 30 days’ notice.',
          ],
        },
        {
          h: '4. Your content',
          p: [
            'You keep all rights to the templates, files and data you upload and the images you generate. You give us a limited licence to store, process and display this content only to provide the Service to you.',
            'You are responsible for having the rights to the content you use, including images, fonts, logos and trademarks.',
          ],
        },
        {
          h: '5. Acceptable use',
          p: [
            'You must not use the Service to create or distribute illegal content, content that infringes others’ rights, malware, spam or deceptive materials, content that exploits minors, or to harass people.',
            'You must not try to break or overload the Service, bypass limits or security, or resell access without our permission. We may suspend accounts that violate these rules.',
          ],
        },
        {
          h: '6. AI features',
          p: [
            'AI results are generated automatically and may be inaccurate or similar to results produced for others. Check them before publishing; you are responsible for how you use them.',
          ],
        },
        {
          h: '7. Free plan and previews',
          p: [
            'Preview renders are free and always carry a watermark. The Free plan is provided as is and its limits may change.',
          ],
        },
        {
          h: '8. Availability',
          p: [
            'We aim for high availability but do not guarantee that the Service will be uninterrupted or error-free. Keep copies of important data; you can export templates at any time.',
          ],
        },
        {
          h: '9. Limitation of liability',
          p: [
            'The Service is provided "as is". To the extent permitted by law, we are not liable for indirect damages, lost profits or lost data, and our total liability is limited to the amount you paid us in the 12 months before the claim.',
          ],
        },
        {
          h: '10. Termination',
          p: [
            'You can delete your workspaces and stop using the Service at any time. We may suspend or terminate access for violation of these terms or non-payment; where possible, we will notify you in advance and let you export your data.',
          ],
        },
        {
          h: '11. Changes and contact',
          p: [
            `We may update these terms; we will notify you about significant changes in advance. Continuing to use the Service after the changes take effect means you accept them. Questions: ${E}.`,
          ],
        },
      ],
    },
    uk: {
      title: 'Умови використання',
      intro: `Ці умови регулюють використання ${C} («Сервіс»). Створюючи акаунт або користуючись Сервісом, ви погоджуєтеся з ними. Якщо ви користуєтеся Сервісом від імені компанії, ви приймаєте умови від її імені.`,
      sections: [
        {
          h: '1. Сервіс',
          p: [
            'Сервіс дає змогу створювати шаблони зображень, генерувати зображення через редактор, API та автоматизації, а також користуватися AI-функціями. Ми можемо змінювати й покращувати функції з часом.',
          ],
        },
        {
          h: '2. Акаунт',
          p: [
            'Ви маєте надавати достовірні дані та зберігати пароль і API-ключі в таємниці. Ви відповідаєте за все, що відбувається через ваш акаунт і простори. Одразу повідомте нас, якщо підозрюєте несанкціонований доступ.',
          ],
        },
        {
          h: '3. Тарифи, оплата та скасування',
          p: [
            'Платні тарифи оплачуються наперед, щомісяця або щороку, і продовжуються автоматично. Платежі обробляє наш реселер Lemon Squeezy (продавець-реєстрант, який також сплачує податки з продажу) або Stripe. За кілька днів до продовження ми надсилаємо нагадування, а після оплати — квитанцію. Ліміти кожного тарифу вказані на сторінці тарифів; коли ліміт вичерпано, повʼязані дії призупиняються до наступного періоду або підвищення тарифу.',
            'Якщо оплата за продовження не пройшла, тариф працює ще кілька днів, поки ви оновлюєте спосіб оплати; якщо оплата так і не надійде, простір перейде на тариф Free. Шаблони й файли зберігаються.',
            'Скасувати підписку можна будь-коли: тариф діє до кінця оплаченого періоду й не продовжується. Платежі не повертаються, крім випадків, передбачених законом. Ми можемо змінювати ціни, попередивши щонайменше за 30 днів.',
          ],
        },
        {
          h: '4. Ваш контент',
          p: [
            'Усі права на шаблони, файли та дані, які ви завантажуєте, і на згенеровані зображення залишаються за вами. Ви надаєте нам обмежену ліцензію зберігати, обробляти й показувати цей контент лише для надання вам Сервісу.',
            'Ви відповідаєте за наявність прав на контент, який використовуєте, зокрема зображення, шрифти, логотипи й торговельні марки.',
          ],
        },
        {
          h: '5. Допустиме використання',
          p: [
            'Не можна використовувати Сервіс для створення чи поширення незаконного контенту, контенту, що порушує права інших, шкідливого ПЗ, спаму чи оманливих матеріалів, контенту, що експлуатує неповнолітніх, або для переслідування людей.',
            'Не можна намагатися зламати чи перевантажити Сервіс, обходити ліміти чи захист або перепродавати доступ без нашого дозволу. Ми можемо призупинити акаунти, що порушують ці правила.',
          ],
        },
        {
          h: '6. AI-функції',
          p: [
            'Результати AI генеруються автоматично й можуть бути неточними або схожими на результати для інших користувачів. Перевіряйте їх перед публікацією; відповідальність за їх використання несете ви.',
          ],
        },
        {
          h: '7. Безкоштовний тариф і превʼю',
          p: [
            'Превʼю-рендери безкоштовні й завжди мають водяний знак. Безкоштовний тариф надається «як є», його ліміти можуть змінюватися.',
          ],
        },
        {
          h: '8. Доступність',
          p: [
            'Ми прагнемо високої доступності, але не гарантуємо безперебійної й безпомилкової роботи Сервісу. Зберігайте копії важливих даних; шаблони можна експортувати будь-коли.',
          ],
        },
        {
          h: '9. Обмеження відповідальності',
          p: [
            'Сервіс надається «як є». У межах, дозволених законом, ми не відповідаємо за непрямі збитки, втрачений прибуток чи втрату даних, а наша загальна відповідальність обмежена сумою, сплаченою вами за 12 місяців до претензії.',
          ],
        },
        {
          h: '10. Припинення',
          p: [
            'Ви можете будь-коли видалити свої простори й припинити користуватися Сервісом. Ми можемо призупинити або припинити доступ за порушення цих умов чи несплату; за можливості попередимо заздалегідь і дамо експортувати дані.',
          ],
        },
        {
          h: '11. Зміни та контакти',
          p: [
            `Ми можемо оновлювати ці умови; про суттєві зміни повідомимо заздалегідь. Подальше користування Сервісом після набрання змінами чинності означає згоду з ними. Питання: ${E}.`,
          ],
        },
      ],
    },
    ru: {
      title: 'Условия использования',
      intro: `Эти условия регулируют использование ${C} («Сервис»). Создавая аккаунт или пользуясь Сервисом, вы соглашаетесь с ними. Если вы пользуетесь Сервисом от имени компании, вы принимаете условия от её имени.`,
      sections: [
        {
          h: '1. Сервис',
          p: [
            'Сервис позволяет создавать шаблоны изображений, генерировать изображения через редактор, API и автоматизации, а также пользоваться AI-функциями. Мы можем менять и улучшать функции со временем.',
          ],
        },
        {
          h: '2. Аккаунт',
          p: [
            'Вы должны указывать достоверные данные и хранить пароль и API-ключи в тайне. Вы отвечаете за всё, что происходит через ваш аккаунт и пространства. Сразу сообщите нам, если подозреваете несанкционированный доступ.',
          ],
        },
        {
          h: '3. Тарифы, оплата и отмена',
          p: [
            'Платные тарифы оплачиваются заранее, ежемесячно или ежегодно, и продлеваются автоматически. Платежи обрабатывает наш реселлер Lemon Squeezy (продавец-регистрант, который также уплачивает налоги с продаж) или Stripe. За несколько дней до продления мы присылаем напоминание, а после оплаты — квитанцию. Лимиты каждого тарифа указаны на странице тарифов; когда лимит исчерпан, связанные действия приостанавливаются до следующего периода или повышения тарифа.',
            'Если оплата за продление не прошла, тариф работает ещё несколько дней, пока вы обновляете способ оплаты; если оплата так и не поступит, пространство перейдёт на тариф Free. Шаблоны и файлы сохраняются.',
            'Отменить подписку можно в любой момент: тариф действует до конца оплаченного периода и не продлевается. Платежи не возвращаются, кроме случаев, предусмотренных законом. Мы можем менять цены, предупредив не менее чем за 30 дней.',
          ],
        },
        {
          h: '4. Ваш контент',
          p: [
            'Все права на шаблоны, файлы и данные, которые вы загружаете, и на сгенерированные изображения остаются за вами. Вы предоставляете нам ограниченную лицензию хранить, обрабатывать и показывать этот контент только для предоставления вам Сервиса.',
            'Вы отвечаете за наличие прав на используемый контент, включая изображения, шрифты, логотипы и товарные знаки.',
          ],
        },
        {
          h: '5. Допустимое использование',
          p: [
            'Нельзя использовать Сервис для создания или распространения незаконного контента, контента, нарушающего права других, вредоносного ПО, спама или вводящих в заблуждение материалов, контента, эксплуатирующего несовершеннолетних, или для преследования людей.',
            'Нельзя пытаться взломать или перегрузить Сервис, обходить лимиты или защиту, перепродавать доступ без нашего разрешения. Мы можем приостановить аккаунты, нарушающие эти правила.',
          ],
        },
        {
          h: '6. AI-функции',
          p: [
            'Результаты AI генерируются автоматически и могут быть неточными или похожими на результаты для других пользователей. Проверяйте их перед публикацией; ответственность за их использование несёте вы.',
          ],
        },
        {
          h: '7. Бесплатный тариф и превью',
          p: [
            'Превью-рендеры бесплатны и всегда содержат водяной знак. Бесплатный тариф предоставляется «как есть», его лимиты могут меняться.',
          ],
        },
        {
          h: '8. Доступность',
          p: [
            'Мы стремимся к высокой доступности, но не гарантируем бесперебойную и безошибочную работу Сервиса. Храните копии важных данных; шаблоны можно экспортировать в любой момент.',
          ],
        },
        {
          h: '9. Ограничение ответственности',
          p: [
            'Сервис предоставляется «как есть». В пределах, допустимых законом, мы не отвечаем за косвенные убытки, упущенную выгоду или потерю данных, а наша общая ответственность ограничена суммой, уплаченной вами за 12 месяцев до претензии.',
          ],
        },
        {
          h: '10. Прекращение',
          p: [
            'Вы можете в любой момент удалить свои пространства и прекратить пользоваться Сервисом. Мы можем приостановить или прекратить доступ за нарушение этих условий или неоплату; по возможности предупредим заранее и дадим экспортировать данные.',
          ],
        },
        {
          h: '11. Изменения и контакты',
          p: [
            `Мы можем обновлять эти условия; о существенных изменениях сообщим заранее. Продолжение использования Сервиса после вступления изменений в силу означает согласие с ними. Вопросы: ${E}.`,
          ],
        },
      ],
    },
  },
};

export const LEGAL_DOCS: Record<LegalKind, Record<Locale, LegalDoc>> = {
  privacy: { ...BASE.privacy, pl: pl.privacy, ro: ro.privacy, es: es.privacy },
  terms: { ...BASE.terms, pl: pl.terms, ro: ro.terms, es: es.terms },
};
